import React from 'react';
import { translate } from 'react-i18next';
import { Platform, StyleSheet, Text, TouchableOpacity, View, ViewStyle} from 'react-native';
import Accordion from 'react-native-collapsible/Accordion';
import { moderateScale } from 'react-native-size-matters';
import Theme from '../../theme';
import { AccordingHeader, ChevronRightIcon } from '../../ui';
import { defaultScale } from '../../utils/scaling';
import { BodyCompositionButton } from './bodycomposition-button';
import {BodyscaleResultBoxAndText} from './bodyscale-assessment-result-box-and-text';
import { mappedBodyTypes } from './bodyscale-bodytype-selector';
interface IProps {
    bodyComposition: string;
    activeSection: number;
    atheleteMode: boolean;
    bodyscaleAccordionSections: BodyscaleAccordionSectionInfo[];
    t: (key: string, options?: any) => string;
    containerStyle: ViewStyle;
    athleteMode: boolean;
    showBodyGroups(): void;
}

interface IState {
    sections: any[];
    activeSection: number;
}

export enum BodyscaleAccordionSectionInfo {
    INSTRUCTIONS= 'INSTRUCTIONS',
    TOUBLESHOOTING= 'TOUBLESHOOTING',
    BODYTYPE = 'BODYTYPE',
    BODYCONDITIONING = 'BODYCONDITIONING',
}
class BodyscaleInfo extends React.PureComponent<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        const {
            t,
            bodyComposition,
            activeSection = 0,
            bodyscaleAccordionSections = [
                BodyscaleAccordionSectionInfo.INSTRUCTIONS,
                BodyscaleAccordionSectionInfo.TOUBLESHOOTING,
            ],
        } = props;

        const bodyscaleAccordionInfo: {[key in BodyscaleAccordionSectionInfo]: any} = {
            INSTRUCTIONS: {
                title: t('instructionsLabel'),
                content: () => this.instructionsContent(),
            },
            TOUBLESHOOTING: {
                title: t('troubleshooting'),
                content: () => this.troubleshootingContent(),
            },
            BODYTYPE: {
                title: `${this.props.t('bodyType')}: ${this.props.t(bodyComposition)}`,
                content: () => this.bodyTypeContent(),
            },
            BODYCONDITIONING: {
                title : `${this.props.t('bodyConditioning')}: ${this.props.athleteMode ?
                    t('athlete') : t('standard')}`,
                content: () => this.BodyConditioningContent(),
            },
        };

        this.state = {
            sections: bodyscaleAccordionSections.map(
                (section: BodyscaleAccordionSectionInfo) => bodyscaleAccordionInfo[section]),
            activeSection,
        };
    }

    public bodyTypeContent = () => {
        const { t, bodyComposition, showBodyGroups } = this.props;
        return (
            <View style = {{alignItems: 'center'}}>
                <BodyscaleResultBoxAndText
                    boxContent={
                    <>
                        {bodyComposition && mappedBodyTypes[bodyComposition].image(Theme.BLACK)}
                        {bodyComposition && (
                        <Text style={{ marginTop: defaultScale(9) }}>
                        {mappedBodyTypes[bodyComposition].title}
                        </Text>
                        )}
                    </>
                    }
                    textContent={bodyComposition ? t(`${bodyComposition}_Explainer`) : ''}
                    resultBoxStyle={styles.resultBoxStyle}
                />
                <BodyCompositionButton showBodyGroups={showBodyGroups} containerStyle={{marginTop: defaultScale(10)}}/>
            </View>
        );
    }

    public BodyConditioningContent = () => {
        const { t, athleteMode, showBodyGroups } = this.props;
        return (
            <View style = {{alignItems: 'center'}}>
                <BodyscaleResultBoxAndText
                    boxContent={<Text>{athleteMode ? t('athlete') : t('standard')}</Text>}
                    textContent={athleteMode ? t('isConditionedAthlete') : t('isNotConditionedAthlete')}
                    resultBoxStyle={styles.resultBoxStyle}
                />
                <BodyCompositionButton showBodyGroups={showBodyGroups} containerStyle={{marginTop: defaultScale(10)}}/>
            </View>
        );
    }

    public instructionsContent = () => {
        const { t } = this.props;
        const instructions = [
            t('instruction1'),
            t('instruction2'),
            t('instruction3'),
            t('instruction4'),
        ];

        return (
            <View>
                <View style={styles.column}>
                    {instructions.map((instruction) => (
                        <View style={[styles.row, styles.instructionRowMargin]}>
                            <View style={styles.bullet}>
                                <ChevronRightIcon width={defaultScale(18)} height={defaultScale(18)} color={Theme.PRIMARY} />
                            </View>
                            <View style={styles.bulletText}>
                                <Text style={styles.contentText}>{instruction}</Text>
                            </View>
                        </View>
                    ))}
                </View>
                <View style={styles.margin}>
                    <View style={styles.divider}></View>
                    <Text style={styles.tipText}>{t('safetyTipHeading')}</Text>
                    <Text style={styles.contentText}>{t('safetyTip')}</Text>
                </View>
            </View>
        );
    }

    public troubleshootingContent = () => {
        const { t } = this.props;
        let troubleshooting = [t('troubleshooting1'), t('troubleshooting2')];
        troubleshooting = Platform.OS === 'ios' ?
            [...troubleshooting, t('troubleshootingBleIOS1'), t('troubleshootingBleIOS2')] :
            [...troubleshooting, t('troubleshootingBleAndroid1'), t('troubleshootingBleAndroid2')];

        return (
            <View style={styles.column}>
                {troubleshooting.map((x) => (
                    <View style={[styles.row, styles.troubleshootingRowMargin]}>
                        <View style={styles.bullet}>
                            <Text style={styles.bulletText}>{'\u2022' + ' '}</Text>
                        </View>
                        <View style={styles.bulletText}>
                            <Text style={styles.contentText}>{x}</Text>
                        </View>
                    </View>
                ))}
            </View>
        );
    }

    public setActiveIndex = (value) => {
        this.setState({ activeSection: value });
    }

    public renderHeader = (section, index, isActive) => (
        <AccordingHeader
            index={index}
            section={section}
            isActive={isActive}
            setActiveIndex={this.setActiveIndex}
            headingStyle={{...Theme.textStyle.bodyCopyBold}}
            titleContainerStyle={ isActive && {paddingBottom: 0}}
        />
    )

    public renderContent = (section) => (
        <View style={styles.content}>{section.content()}</View>
    )

    public render() {
        return (
            <View style={[styles.accordionContainer, this.props.containerStyle]}>
                <Accordion
                    sections={this.state.sections}
                    activeSection={this.state.activeSection}
                    renderHeader={this.renderHeader}
                    renderContent={this.renderContent}
                    touchableComponent={TouchableOpacity}
                    initiallyActiveSection={0}
                />
            </View>
        );
    }
}

const styles = StyleSheet.create({
    accordionContainer: {
        marginTop: moderateScale(40, 1),
    },
    content: {
        paddingBottom: defaultScale(15),
    },

    column: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginTop: defaultScale(25),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        flex: 1,
    },
    bullet: {
        marginRight: defaultScale(20),
    },
    bulletText: {
        ...Theme.textStyle.bodyCopyReg,
        flex: 1,
    },
    contentText: {
        ...Theme.textStyle.bodyCopyReg,
        color: Theme.GREY_DARK_MEDIUM,
    },
    instructionRowMargin: {
        marginBottom: defaultScale(20),
    },
    troubleshootingRowMargin: {
        marginBottom: defaultScale(5),
    },
    tipText: {
        ...Theme.textStyle.bodyCopyMed,
        marginTop: defaultScale(5),
    },
    divider: {
        paddingBottom: defaultScale(8),

        borderTopWidth: 1,
        borderTopColor: Theme.GREY_LIGHT,
    },
    margin: {
        margin: defaultScale(8),
    },
    resultBoxStyle: {
        marginTop: defaultScale(25),
        marginBottom: defaultScale(13),
    },
});

export default translate(['bodyscan'])(BodyscaleInfo);
